import React from "react";
import { useHistory, useParams } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Checkbox from "@material-ui/core/Checkbox";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import IconButton from "@material-ui/core/IconButton";
import CircularProgress from "@material-ui/core/CircularProgress";
import ArrowBack from "@material-ui/icons/ArrowBack";
import Remove from "@material-ui/icons/Remove";
import Add from "@material-ui/icons/Add";

const GRAPHQL_URL = (process.env.REACT_APP_API_URL || "http://localhost:5001") + "/graphql";
const QUICK_AMOUNTS = [1000, 2000, 5000, 10000, 20000, 50000, 100000, 200000];

const useStyles = makeStyles({
  page: {
    display: "flex", height: "calc(100vh - 80px)", gap: "0",
    fontFamily: "'Roboto','Helvetica','Arial',sans-serif",
  },
  left: {
    flex: 1, overflowY: "auto", padding: "16px",
    borderRight: "1px solid #e0e0e0", backgroundColor: "#fafafa",
  },
  right: {
    width: "340px", flexShrink: 0, display: "flex", flexDirection: "column",
    backgroundColor: "#fff", padding: "16px", overflowY: "auto",
  },
  header: {
    display: "flex", alignItems: "center", gap: "8px", marginBottom: "14px",
  },
  lineRow: {
    display: "flex", alignItems: "center", padding: "10px 12px",
    borderBottom: "1px solid #f0f0f0", gap: "8px", backgroundColor: "#fff",
    borderRadius: "4px", marginBottom: "4px",
    border: "1px solid #eeeeee",
  },
  lineRowDisabled: { opacity: 0.45 },
  lineMeta: { flex: 1, minWidth: 0 },
  lineCode: { fontSize: "11px", color: "#888" },
  lineName: { fontSize: "13px", fontWeight: 600, color: "#222" },
  qtyWrapper: {
    display: "flex", alignItems: "center", gap: "4px",
  },
  qtyBtn: {
    width: "26px", height: "26px", borderRadius: "50%", border: "1px solid #ccc",
    display: "flex", alignItems: "center", justifyContent: "center",
    cursor: "pointer", backgroundColor: "#fff", fontSize: "16px", userSelect: "none",
    flexShrink: 0,
  },
  qtyInput: {
    width: "36px", textAlign: "center", border: "none",
    borderBottom: "1px solid #bbb", fontSize: "14px", fontWeight: 600,
    outline: "none", backgroundColor: "transparent",
  },
  qtyMax: { fontSize: "11px", color: "#aaa", marginLeft: "2px" },
  priceCol: { textAlign: "right", minWidth: "70px", fontSize: "13px", color: "#555" },
  totalCol: { textAlign: "right", minWidth: "72px", fontWeight: 700, fontSize: "14px" },
  // Right panel
  customerBox: {
    padding: "10px 12px", backgroundColor: "#e3f2fd",
    borderRadius: "6px", marginBottom: "10px",
  },
  invoiceRef: { marginBottom: "12px", fontSize: "14px" },
  summaryRow: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "7px 0", borderBottom: "1px solid #f5f5f5", fontSize: "14px",
  },
  summaryTotal: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "10px 0", fontSize: "16px", fontWeight: 700, color: "#1565c0",
  },
  editInput: {
    border: "none", borderBottom: "1px solid #bbb", outline: "none",
    fontSize: "14px", textAlign: "right", width: "110px", padding: "2px 0",
    backgroundColor: "transparent",
  },
  payMethod: { display: "flex", gap: "6px", flexWrap: "wrap", margin: "8px 0" },
  quickAmounts: { display: "flex", flexWrap: "wrap", gap: "6px", margin: "8px 0" },
  quickBtn: {
    padding: "5px 12px", borderRadius: "20px", border: "1px solid #ccc",
    cursor: "pointer", fontSize: "12px", backgroundColor: "#fff",
  },
  returnBtnWrapper: { marginTop: "auto", paddingTop: "14px" },
  noteInput: {
    width: "100%", border: "1px solid #ccc", borderRadius: "4px",
    padding: "6px 8px", fontSize: "13px", resize: "vertical", minHeight: "52px",
    outline: "none", fontFamily: "inherit", boxSizing: "border-box",
  },
});

function gql(query, variables) {
  const token = localStorage.getItem("token");
  return fetch(GRAPHQL_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: token ? `Bearer ${token}` : "" },
    credentials: "include",
    body: JSON.stringify({ query, variables }),
  }).then((r) => r.json());
}

function formatNum(val) {
  return Number(val || 0).toLocaleString("vi-VN");
}

export default function ReturnInvoicePage() {
  const classes = useStyles();
  const history = useHistory();
  const { id } = useParams();

  const [invoice, setInvoice] = React.useState(null);
  const [lines, setLines] = React.useState([]); // [{detail, checked, returnQty}]
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [success, setSuccess] = React.useState("");

  const [discount, setDiscount] = React.useState(0);
  const [returnFee, setReturnFee] = React.useState(0);
  const [surchargeRefund, setSurchargeRefund] = React.useState(0);
  const [note, setNote] = React.useState("");
  const [payMethod, setPayMethod] = React.useState("cash");

  // Fetch invoice + line items in parallel
  React.useEffect(() => {
    setLoading(true);
    Promise.all([
      gql(
        `query Q($id:String!){getInvoice(id:$id){id invoiceNumber invoiceDate status subtotal discount surcharges total paid customer{id customerNumber name phone1 address currentDebitAmount}}}`,
        { id }
      ),
      gql(
        `query Q($invoiceId:String!){getInvoiceDetails(invoiceId:$invoiceId){id productNumber productName barcode brand unit quantity salePrice total discountPercent discount price}}`,
        { invoiceId: id }
      ),
    ])
      .then(([invRes, detRes]) => {
        if (invRes.errors) { setError(invRes.errors[0].message); return; }
        if (detRes.errors) { setError(detRes.errors[0].message); return; }
        const inv = invRes.data?.getInvoice;
        if (!inv) { setError("Invoice not found"); return; }
        setInvoice(inv);
        if (inv.surcharges) setSurchargeRefund(parseFloat(inv.surcharges) || 0);
        const details = detRes.data?.getInvoiceDetails || [];
        setLines(details.map((d) => {
          const origQty = Number(d.quantity);
          return {
            detail: d,
            checked: false,
            returnQty: origQty > 0 ? origQty : 1,
            note: "",
          };
        }));
      })
      .catch(() => setError("Cannot connect to server"))
      .finally(() => setLoading(false));
  }, [id]);

  const totalAmountReturned = lines.reduce((acc, l) => {
    if (!l.checked) return acc;
    return acc + l.returnQty * (Number(l.detail.salePrice) || 0);
  }, 0);

  const totalRefund = Math.max(0, totalAmountReturned - discount - returnFee + surchargeRefund);

  const handleQty = (idx, delta) => {
    setLines((prev) => prev.map((l, i) => {
      if (i !== idx) return l;
      const max = Number(l.detail.quantity) > 0 ? Number(l.detail.quantity) : 1;
      return { ...l, returnQty: Math.max(1, Math.min(max, l.returnQty + delta)) };
    }));
  };

  const handleQtyInput = (idx, val) => {
    setLines((prev) => prev.map((l, i) => {
      if (i !== idx) return l;
      const max = Number(l.detail.quantity) > 0 ? Number(l.detail.quantity) : 1;
      const n = Number(val);
      return { ...l, returnQty: Math.max(1, Math.min(max, n > 0 ? n : 1)) };
    }));
  };

  const handleCheck = (idx) => {
    setLines((prev) => prev.map((l, i) => i === idx ? { ...l, checked: !l.checked } : l));
  };

  const handleLineNote = (idx, val) => {
    setLines((prev) => prev.map((l, i) => i === idx ? { ...l, note: val } : l));
  };

  const handleReturn = async () => {
    if (!invoice || totalAmountReturned <= 0) return;
    setSubmitting(true);
    try {
      const selectedLines = lines
        .filter((l) => l.checked)
        .map((l) => ({
          productId: l.detail.productId || undefined,
          productNumber: l.detail.productNumber || undefined,
          productName: l.detail.productName,
          barcode: l.detail.barcode || undefined,
          brand: l.detail.brand || undefined,
          unit: l.detail.unit || undefined,
          productNote: l.note || undefined,
          quantity: l.returnQty,
          price: Number(l.detail.price),
          discountPercent: Number(l.detail.discountPercent) || 0,
          discount: Number(l.detail.discount) || 0,
          salePrice: Number(l.detail.salePrice),
          total: l.returnQty * Number(l.detail.salePrice),
        }));

      const { data, errors } = await gql(
        `mutation M($input:CreateReturnDto!){createReturn(input:$input){id invoiceNumber}}`,
        {
          input: {
            originalInvoiceId: invoice.id,
            totalAmountReturned,
            discount,
            returnFee,
            surchargeRefund,
            totalRefund,
            note: note || undefined,
            lines: selectedLines,
          },
        }
      );
      if (errors) { alert(errors[0].message); return; }
      setSuccess(`Return invoice ${data.createReturn.invoiceNumber} created!`);
      setTimeout(() => history.push("/admin/invoices"), 1500);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "80px" }}>
        <CircularProgress />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ color: "#f44336", textAlign: "center", padding: "60px", fontSize: "15px" }}>
        {error}
      </div>
    );
  }

  const customer = invoice?.customer;

  return (
    <div className={classes.page}>
      {/* ── LEFT: product line items ── */}
      <div className={classes.left}>
        <div className={classes.header}>
          <IconButton size="small" onClick={() => history.push("/admin/invoices")}>
            <ArrowBack fontSize="small" />
          </IconButton>
          <span style={{ fontSize: "13px", color: "#666" }}>Back to Invoices</span>
        </div>

        {/* Column header */}
        <div style={{
          display: "flex", alignItems: "center", padding: "4px 12px",
          fontSize: "11px", color: "#aaa", fontWeight: 600, gap: "8px",
          marginBottom: "6px",
        }}>
          <div style={{ width: "32px" }} />
          <div style={{ flex: 1 }}>Product</div>
          <div style={{ minWidth: "130px", textAlign: "center" }}>Return qty / Original</div>
          <div style={{ minWidth: "70px", textAlign: "right" }}>Unit price</div>
          <div style={{ minWidth: "72px", textAlign: "right" }}>Amount</div>
        </div>

        {lines.length === 0 && (
          <div style={{ textAlign: "center", color: "#aaa", padding: "40px", fontSize: "14px" }}>
            No line items found for this invoice
          </div>
        )}

        {lines.map((l, idx) => {
          const d = l.detail;
          const maxQty = Number(d.quantity) > 0 ? Number(d.quantity) : 1;
          const lineTotal = l.checked ? l.returnQty * (Number(d.salePrice) || 0) : 0;
          return (
            <div
              key={d.id}
              className={`${classes.lineRow} ${!l.checked ? classes.lineRowDisabled : ""}`}
            >
              <Checkbox
                checked={l.checked}
                onChange={() => handleCheck(idx)}
                color="primary"
                size="small"
                style={{ padding: "4px" }}
              />
              <div className={classes.lineMeta}>
                {d.productNumber && (
                  <div className={classes.lineCode}>{d.productNumber}</div>
                )}
                <div className={classes.lineName}>{d.productName}</div>
                {d.unit && (
                  <div style={{ fontSize: "11px", color: "#aaa" }}>{d.unit}</div>
                )}
                {l.checked && (
                  <input
                    type="text"
                    value={l.note}
                    onChange={(e) => handleLineNote(idx, e.target.value)}
                    placeholder="Note for this item..."
                    style={{
                      marginTop: "5px", width: "100%", fontSize: "12px",
                      border: "none", borderBottom: "1px solid #ddd", outline: "none",
                      padding: "2px 0", backgroundColor: "transparent", color: "#555",
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                )}
              </div>

              {/* Qty stepper */}
              <div className={classes.qtyWrapper}>
                <div
                  className={classes.qtyBtn}
                  onClick={() => l.checked && handleQty(idx, -1)}
                  style={{ opacity: l.checked ? 1 : 0.4 }}
                >
                  <Remove style={{ fontSize: "14px" }} />
                </div>
                <input
                  className={classes.qtyInput}
                  type="number"
                  min="1"
                  max={maxQty}
                  value={l.returnQty}
                  disabled={!l.checked}
                  onChange={(e) => handleQtyInput(idx, e.target.value)}
                />
                <span className={classes.qtyMax}>/ {maxQty}</span>
                <div
                  className={classes.qtyBtn}
                  onClick={() => l.checked && handleQty(idx, 1)}
                  style={{ opacity: l.checked ? 1 : 0.4 }}
                >
                  <Add style={{ fontSize: "14px" }} />
                </div>
              </div>

              <div className={classes.priceCol}>{formatNum(d.salePrice)}</div>
              <div
                className={classes.totalCol}
                style={{ color: l.checked ? "#1565c0" : "#aaa" }}
              >
                {formatNum(lineTotal)}
              </div>
            </div>
          );
        })}

        {/* Note */}
        <div style={{ marginTop: "16px" }}>
          <div style={{ fontSize: "12px", color: "#888", marginBottom: "4px" }}>Note</div>
          <textarea
            className={classes.noteInput}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Return reason..."
          />
        </div>
      </div>

      {/* ── RIGHT: summary panel ── */}
      <div className={classes.right}>
        {/* Customer info */}
        {customer && (
          <div className={classes.customerBox}>
            <div style={{ fontWeight: 600, fontSize: "14px" }}>
              {customer.name}
              {customer.phone1 && (
                <span style={{ marginLeft: "8px", fontWeight: 400, color: "#555", fontSize: "13px" }}>
                  {customer.phone1}
                </span>
              )}
            </div>
            {customer.address && (
              <div style={{ fontSize: "12px", color: "#777", marginTop: "2px" }}>
                {customer.address}
              </div>
            )}
            {customer.currentDebitAmount != null && (
              <div style={{ marginTop: "4px" }}>
                <span style={{
                  display: "inline-block", padding: "1px 8px", borderRadius: "10px",
                  fontSize: "12px", fontWeight: 700,
                  backgroundColor: customer.currentDebitAmount > 0 ? "#fce4ec" : "#e8f5e9",
                  color: customer.currentDebitAmount > 0 ? "#b71c1c" : "#2e7d32",
                }}>
                  Debt: {formatNum(customer.currentDebitAmount)}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Invoice reference */}
        <div className={classes.invoiceRef}>
          <span style={{ fontWeight: 700, color: "#e65100", fontSize: "15px" }}>Return</span>
          <span style={{ color: "#bbb", margin: "0 5px" }}>/</span>
          <span style={{ fontWeight: 700, color: "#1565c0", fontSize: "14px" }}>
            {invoice?.invoiceNumber}
          </span>
          {customer?.name && (
            <span style={{ color: "#666", fontSize: "13px" }}> — {customer.name}</span>
          )}
        </div>

        {/* Success */}
        {success && (
          <div style={{
            padding: "8px 12px", backgroundColor: "#e8f5e9", color: "#2e7d32",
            borderRadius: "6px", marginBottom: "10px", fontWeight: 600, fontSize: "13px",
          }}>
            ✓ {success}
          </div>
        )}

        {/* Summary */}
        <div style={{ borderTop: "1px solid #eee" }}>
          <div className={classes.summaryRow}>
            <span style={{ color: "#666" }}>Total original purchase</span>
            <span>{formatNum(parseFloat(invoice?.total) || 0)}</span>
          </div>

          <div className={classes.summaryRow}>
            <span style={{ color: "#333" }}>Total amount returned</span>
            <span style={{ fontWeight: 600 }}>{formatNum(totalAmountReturned)}</span>
          </div>

          <div className={classes.summaryRow}>
            <span>Discount</span>
            <input
              className={classes.editInput}
              type="number"
              min="0"
              value={discount}
              onChange={(e) => setDiscount(parseFloat(e.target.value) || 0)}
            />
          </div>

          <div className={classes.summaryRow}>
            <span>Return fee</span>
            <input
              className={classes.editInput}
              type="number"
              min="0"
              value={returnFee}
              onChange={(e) => setReturnFee(parseFloat(e.target.value) || 0)}
            />
          </div>

          <div className={classes.summaryRow}>
            <span>Surcharge refund</span>
            <input
              className={classes.editInput}
              type="number"
              min="0"
              value={surchargeRefund}
              onChange={(e) => setSurchargeRefund(parseFloat(e.target.value) || 0)}
            />
          </div>

          <div className={classes.summaryTotal}>
            <span>Total refund</span>
            <span>{formatNum(totalRefund)}</span>
          </div>

          <div className={classes.summaryRow} style={{ borderBottom: "none" }}>
            <span style={{ fontWeight: 600 }}>Refund amount</span>
            <span style={{ fontWeight: 700, fontSize: "15px" }}>{formatNum(totalRefund)}</span>
          </div>
        </div>

        {/* Payment method */}
        <div style={{ marginTop: "10px" }}>
          <RadioGroup
            row
            value={payMethod}
            onChange={(e) => setPayMethod(e.target.value)}
            className={classes.payMethod}
          >
            {[
              { value: "cash", label: "Cash" },
              { value: "transfer", label: "Bank transfer" },
              { value: "card", label: "Card" },
              { value: "wallet", label: "Wallet" },
            ].map((m) => (
              <FormControlLabel
                key={m.value}
                value={m.value}
                control={<Radio color="primary" size="small" />}
                label={<span style={{ fontSize: "13px" }}>{m.label}</span>}
                style={{ marginRight: "6px", marginBottom: "2px" }}
              />
            ))}
          </RadioGroup>
        </div>

        {/* Quick amounts */}
        <div className={classes.quickAmounts}>
          {QUICK_AMOUNTS.map((amt) => (
            <button key={amt} className={classes.quickBtn}>
              {formatNum(amt)}
            </button>
          ))}
        </div>

        {/* Return button */}
        <div className={classes.returnBtnWrapper}>
          <button
            onClick={handleReturn}
            disabled={submitting || totalAmountReturned <= 0}
            style={{
              width: "100%", padding: "14px", borderRadius: "8px",
              backgroundColor: submitting || totalAmountReturned <= 0 ? "#ccc" : "#1565c0",
              color: "#fff", border: "none", fontSize: "15px", fontWeight: 700,
              cursor: submitting || totalAmountReturned <= 0 ? "not-allowed" : "pointer",
              letterSpacing: "1px", textTransform: "uppercase",
              display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
            }}
          >
            {submitting ? <CircularProgress size={18} style={{ color: "#fff" }} /> : "RETURN"}
          </button>
        </div>
      </div>
    </div>
  );
}
